import { createFileRoute } from '@tanstack/react-router'

import {
  EarningsChart,
  PayoutReceipt,
  PremiumSheet,
  RewardsSheet,
} from '@/components/fotograf/vydelky'
import { JellyIcon } from '@/components/icons'
import { SectionLabel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import {
  APRIL_PREMIUM_CONDITIONS,
  APRIL_ROWS,
  APRIL_STATS,
  APRIL_TOTAL,
  MONTHS,
  PAST_MONTHS,
  PENALTIES,
  PREMIUM_AMOUNT,
  PREMIUM_RULES,
  RATES,
} from '@/mocks/fotograf/vydelky'

/** Both sheets are state-in-URL, so a sheet is linkable and closing it is a link. */
type VydelkySearch = { info?: 'odmeny' | 'premie' }

export const Route = createFileRoute('/fotograf/vydelky')({
  validateSearch: (search: Record<string, unknown>): VydelkySearch =>
    search.info === 'odmeny' || search.info === 'premie' ? { info: search.info } : {},
  head: () => ({ meta: [{ title: 'Výdělky · Aplikace Bitez' }] }),
  component: FotografVydelky,
})

const PAGE_HREF = '/fotograf/vydelky'
/* Two different questions, two sheets: the standing rules ("?" in the header)
   and this month's premium (the badge on the receipt). */
const RULES_HREF = `${PAGE_HREF}?info=odmeny`
const PREMIUM_HREF = `${PAGE_HREF}?info=premie`

function FotografVydelky() {
  const { info } = Route.useSearch()

  return (
    <div className="px-4 pt-6 pb-6">
      <header className="relative pt-6 text-center">
        <Link
          href={RULES_HREF}
          aria-label="Jak se počítá výplata"
          className="absolute top-0 right-0 flex size-8 items-center justify-center rounded-full bg-white shadow-sm transition-colors duration-150 hover:bg-surface"
        >
          <JellyIcon name="circle-question" className="size-4 text-ink/55" />
        </Link>

        {/* Centred and given room: on this screen the money IS the headline,
            not a page title. The sticker belongs to the MONTH, so it sits with
            the month rather than with the number. */}
        <SectionLabel>Duben 2026</SectionLabel>

        <span className="mt-2 inline-flex -rotate-3 items-center gap-1 rounded-full bg-sun px-2.5 py-1 text-[10px] font-bold whitespace-nowrap">
          <JellyIcon name="star" variant="fill" className="size-2.5" />
          Nejlepší měsíc
        </span>

        <p className="font-display mt-2 text-5xl leading-[0.85]">{APRIL_TOTAL}</p>

        <div className="mt-5 flex justify-center gap-5">
          {APRIL_STATS.map((stat) => (
            <span
              key={stat.label}
              className="flex items-center gap-1.5 text-xs font-semibold text-ink/60"
            >
              <JellyIcon
                name={stat.icon}
                variant={stat.icon === 'star' ? 'fill' : 'line'}
                className={`size-3.5 ${stat.icon === 'star' ? 'text-sun-dark' : 'text-ink/45'}`}
              />
              {stat.label}
            </span>
          ))}
        </div>
      </header>

      <div className="animate-rise-up mt-6">
        <PayoutReceipt
          month="Duben 2026"
          rows={APRIL_ROWS}
          total={APRIL_TOTAL}
          note="Děkujeme za skvělou práci"
          bonusHref={PREMIUM_HREF}
        />
      </div>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '60ms' }}>
        <SectionLabel>Posledních 6 měsíců</SectionLabel>
        <div className="mt-4">
          <EarningsChart months={MONTHS} />
        </div>
      </section>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '120ms' }}>
        <SectionLabel>Předchozí účtenky</SectionLabel>
        <div className="mt-3 space-y-3">
          {PAST_MONTHS.map((entry) => (
            <button
              key={entry.month}
              type="button"
              className="group flex w-full cursor-pointer items-baseline gap-1.5 text-left"
            >
              <span className="font-display text-sm uppercase">{entry.month}</span>
              <span
                aria-hidden
                className="min-w-4 flex-1 self-center border-b-2 border-dotted border-ink/15"
              />
              <span className="text-xs font-bold">{entry.total}</span>
              <JellyIcon
                name="arrow-right"
                className="size-3 self-center text-ink/40 transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </button>
          ))}
        </div>
      </section>

      {info === 'odmeny' && (
        <RewardsSheet
          closeHref={PAGE_HREF}
          rates={RATES}
          premiumAmount={PREMIUM_AMOUNT}
          premiumRules={PREMIUM_RULES}
          penalties={PENALTIES}
        />
      )}

      {info === 'premie' && (
        <PremiumSheet
          closeHref={PAGE_HREF}
          month="duben"
          amount="+500 Kč"
          conditions={APRIL_PREMIUM_CONDITIONS}
          note="Prémie je součástí dubnové výplaty. Pravidla najdete pod otazníkem nahoře."
        />
      )}
    </div>
  )
}
